import React, { useState } from 'react'
import { useHistory } from 'react-router-dom'
import { Formik } from 'formik'
import {
	FormContainer,
	BackButton,
	FormHeader,
	FormTitle,
	FormSubtitle,
	ButtonsContainer,
	LinkButton,
	FormText,
	FormSubtitleBold,
	EmptySpace,
	HeaderDesktop,
	HeaderMobile,
	MobileBackButton,
	TextSteap
} from '../../ui/form'
import { Button } from '../../ui/button'
import Feedback from '../../feedback'
import { useSignUpData, useSignUpSubmit } from '../../../hooks/useSignUpData'
import {
	getValidationSchemaPage1,
	getValidationSchemaPage2,
	getValidationSchemaPage3,
	isPageValid
} from './validations'
import { TOTAL_STEPS, FORM_TEXTS } from '../signUp/constants'
import { ChevronLeft, ErrorIcon } from '../../../assets/icons'
import SignUpStep1 from './Step1'
import SignUpStep2 from './Step2'
import SignUpStep3 from './Step3'
import useIsMobile from '../../../hooks/useIsMobile'
import theme from '../../../styled/theme'

export const initialValues = {
	name: '',
	lastName: '',
	email: '',
	repeatEmail: '',
	documentType: '',
	documentNumber: '',
	gender: '',
	birthDate: '',
	credentialNumber: '',
	healthInsurance: '',
	plan: '',
	phoneNumber: ''
}

const SignUpForm = () => {
	const history = useHistory()
	const [numberPage, setNumberPage] = useState(1)
	const [messageResult, setMessageResult] = useState(false)
	const [errorResult, setErrorResult] = useState(false)
	const isMobile = useIsMobile();

	const {
		genders,
		documentTypes,
		healthInsurances,
		plans,
		loading,
		error: loadingError,
		getHealthInsurancePlans
	} = useSignUpData()

	const { handleSubmit: submitSignUp, submitting } = useSignUpSubmit()

	const onClickButton = () => {
		history.push('/')
	}

	const handleBack = () => {
		if (numberPage <= 1) {
			onClickButton()
		} else {
			setNumberPage(prev => Math.max(1, prev - 1))
		}
	}

	const genderOptions =
		genders &&
		genders.map(g => ({
			value: g.id,
			label: g.valueText
		}))

	const documentTypeOptions =
		documentTypes &&
		documentTypes.map(dt => ({
			value: dt.id,
			label: dt.valueText
		}))

	const healthInsuranceOptions =
		healthInsurances &&
		healthInsurances.map(hi => ({
			value: hi.codigo ?? hi.id ?? hi.value,
			label: hi.descripcion ?? hi.valueText ?? hi.label
		}))

	const planOptions =
		plans &&
		plans.map(p => ({
			value: String(p.codigo),
			label: p.descripcion
		}))

	const getValidationSchema = () => {
		if (numberPage === 1) return getValidationSchemaPage1()
		if (numberPage === 2) return getValidationSchemaPage2()
		return getValidationSchemaPage3(plans.length > 0)
	}

	const handleFormSubmit = async (values, { setSubmitting }) => {
		if (numberPage < TOTAL_STEPS) {
			setNumberPage(prev => prev + 1)
			setSubmitting(false)
		} else {
			const result = await submitSignUp(values)
			if (result.success) {
				setNumberPage(4)
				setMessageResult(true)
				setErrorResult(false)
			} else {
				setErrorResult(true)
				setMessageResult(false)
			}
			setSubmitting(false)
		}
	}

	if (loadingError) {
		return (
			<FormContainer paddingMobile={'0px 10px'} gap={32}>
				<FormHeader align="center" gap={16}>
					<ErrorIcon />
					<FormText>Error al cargar los datos del formulario</FormText>
					<Button label="Reintentar" $variant="primary" onClick={() => window.location.reload()} />
				</FormHeader>
			</FormContainer>
		)
	}

	return (
		<>
			{numberPage <= TOTAL_STEPS ? (
				<Formik
					initialValues={initialValues}
					validationSchema={getValidationSchema()}
					onSubmit={handleFormSubmit}
				>
					{({ values, errors, touched, handleChange, handleBlur, handleSubmit, setFieldValue }) => (
						<FormContainer
							justifyMobile={'between'}
							paddingMobile={'20px 32px 15px 32px'}
							gap={isMobile ? 20 : 32}
						>
							<div style={{ width: '100%' }}>
								<HeaderDesktop>
									<BackButton onClick={handleBack}>
										<ChevronLeft />
										<LinkButton>{FORM_TEXTS.buttons.back}</LinkButton>
									</BackButton>
									<FormHeader align="start" gap={4} mb={0}>
										<FormText align="left" mb={8}>
											Paso {numberPage} de {TOTAL_STEPS}
										</FormText>
										<FormTitle align="left" mb={0}>
											{FORM_TEXTS.title}
										</FormTitle>
										<FormSubtitle align="left">{FORM_TEXTS.subtitle}</FormSubtitle>
									</FormHeader>
								</HeaderDesktop>
								<HeaderMobile>
									<MobileBackButton onClick={handleBack}>
										<ChevronLeft color={theme.colors.black} />
									</MobileBackButton>
									<TextSteap>
										<FormSubtitleBold>{FORM_TEXTS.mobileTitle}</FormSubtitleBold>
										<FormText>
											Paso {numberPage} de {TOTAL_STEPS}
										</FormText>
									</TextSteap>
									<EmptySpace />
								</HeaderMobile>
								{isMobile &&
									<div style={{ marginBottom: '32px' }}>
										<FormTitle align="left" mb={0}>
											{FORM_TEXTS.title}
										</FormTitle>
										<FormSubtitle align="left">{FORM_TEXTS.subtitle}</FormSubtitle>
									</div>
								}
								{numberPage === 1 && (
									<SignUpStep1
										values={values}
										errors={errors}
										touched={touched}
										handleChange={handleChange}
										handleBlur={handleBlur}
									/>
								)}

								{numberPage === 2 && (
									<SignUpStep2
										values={values}
										errors={errors}
										touched={touched}
										handleChange={handleChange}
										handleBlur={handleBlur}
										setFieldValue={setFieldValue}
										genderOptions={genderOptions}
										documentTypeOptions={documentTypeOptions}
									/>
								)}

								{numberPage === 3 && (
									<SignUpStep3
										values={values}
										errors={errors}
										touched={touched}
										handleChange={handleChange}
										handleBlur={handleBlur}
										setFieldValue={setFieldValue}
										getHealthInsurancePlans={getHealthInsurancePlans}
										healthInsuranceOptions={healthInsuranceOptions}
										planOptions={planOptions}
									/>
								)}
							</div>
							<ButtonsContainer gap={isMobile ? 16 : 24}>
								<Button
									label={
										numberPage === TOTAL_STEPS
											? FORM_TEXTS.buttons.register
											: FORM_TEXTS.buttons.continue
									}
									$variant="primary"
									size="mobile"
									type="submit"
									disabled={
										!isPageValid(values, errors, numberPage, plans.length > 0) || submitting
									}
									onClick={handleSubmit}
								/>
								<LinkButton onClick={onClickButton}>{FORM_TEXTS.buttons.cancel}</LinkButton>
							</ButtonsContainer>
						</FormContainer>
					)}
				</Formik>
			) : messageResult && !errorResult ? (
				<Feedback
					title="Te enviamos un correo electrónico para que termines tu registro"
					buttonLabel="Volver al inicio"
					onClickButton={onClickButton}
				/>
			) : (
				<Feedback
					title="Hubo un error al terminar tu registro"
					bodyText='Por favor volvé a intentarlo más tarde'
					buttonLabel="Volver al inicio"
					onClickButton={onClickButton}
				/>
			)}
		</>
	)
}

export default SignUpForm
